import { useCallback, useMemo, useState } from 'react';
import { Slider } from '../../../../components/Slider/Slider';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from '../../../../components/Loader/Loader';
import { Button } from 'react-bootstrap';
import {
    getLiteTradeAssets,
    makeSelectorGetLiteTradeAccountOrderInfo,
} from '../../../../modules/LiteTrade/liteTradeSelectors';
import { cancelLiteTradeOrderAction } from '../../../../modules/LiteTrade/liteTradeActions';
import {
    SlideMobileModalBoxStyled,
    SlideMobileModalHeaderStyled,
    SlideMobileModalInfoRowStyled,
} from './styled/SlideMobileStyled';
import BinIcon from '../../../../components/icon/Icon';
import IconAsset from '../../../../components/icon/IconAsset';
import LiteTradePortfolioOrderInfo from '../../../../modules/LiteTrade/components/LiteTradePortfolio/LiteTradePortfolioOrderInfo';
import { LITE_TRADE_POSITION_SIDE } from '../../../../modules/LiteTrade/liteTradeConstants';
import { openMobileSliderPageAction } from '../../../mobileAppActions';
import { IMobileSliderPageId, MOBILE_SLIDER_PAGES_IDS } from '../mobileSliderPagesConstants';
import { makeSelectorMobilePositionUpdateProtectionOrderInfo } from '../../MobilePosition/MobilePositionUpdate/mobilePositionUpdateSelectors';
import { ORDER_TYPES } from '../../../../components/OrderTypeSelector/orderTypeSelectorConstants';
import { useLangContext } from '../../../../providers/LangProvider/LangProviderContext';
import type {
    ILiteTradeAsset,
    ILiteTradeAssetSymbol,
    ILiteTradePortfolioOrderStatus,
    IPortfolioOrderId,
} from '../../../../modules/LiteTrade/types/liteTradeReducer.t';
import type Asset from '../../../../modules/Asset/Asset';
import { MobilePositionUnavailableStyled } from '../../MobilePosition/styled/MobilePositionStyled';
import _ from 'lodash';
import { FEATURES } from '../../../../app/appConstants';
import { getAppFeatures } from '../../../../app/appSelectors';
import { AB_TESTS_NAMES, AB_TEST_OPEN_ORDER_V2_OPTIONS } from '../../../../modules/AbTests/abTestsConstants';
import { useAbTestOption } from '../../../../modules/AbTests/abTestsHooks';
import { useLocation } from 'react-router';
import { SlideMobileOrderModalAssetBoxStyled } from './styled/SlideMobileOrderModalStyled';
import { AssetRelevantPrice } from '../../../../modules/Asset/components/AssetRelevantPrice';
import { locationSlice } from '../../../../modules/Location/locationReducer';
import { LOCATION_NAMES } from '../../../../modules/Location/locationConstants';

interface ISlideMobileOrderModalProps {
    symbol: ILiteTradeAssetSymbol;
    assetInstance: Asset;
    openAssetOnClick?: boolean;
    onClose: (action: string) => void;
    orderId: IPortfolioOrderId;
    positionCode?: ILiteTradePortfolioOrderStatus;
}

const SlideMobileOrderModal = (props: ISlideMobileOrderModalProps) => {
    const { symbol, assetInstance, openAssetOnClick, onClose, orderId, positionCode } = props;
    const location = useLocation();
    const features = useSelector(getAppFeatures);
    const openOrderV2AbTestOption = useAbTestOption(AB_TESTS_NAMES.OPEN_ORDER_V2);
    const isOpenOrderV2AbTestOptionActive = openOrderV2AbTestOption !== undefined;
    const assets = useSelector(getLiteTradeAssets);
    const orderInfo = useSelector(makeSelectorGetLiteTradeAccountOrderInfo(orderId));
    const orderStopLoss = useSelector(
        makeSelectorMobilePositionUpdateProtectionOrderInfo({
            orderId: orderId,
            positionCode: positionCode,
            protectionOrderType: ORDER_TYPES.STOP,
        }),
    );
    const orderTakeProfit = useSelector(
        makeSelectorMobilePositionUpdateProtectionOrderInfo({
            orderId: orderId,
            positionCode: positionCode,
            protectionOrderType: ORDER_TYPES.LIMIT,
        }),
    );
    const dispatch = useDispatch();
    const withSpread = orderInfo?.side === LITE_TRADE_POSITION_SIDE.BUY;
    const cancelOrder = useCallback((payload) => dispatch(cancelLiteTradeOrderAction(payload)), [dispatch]);
    const openMobileSliderPage = useCallback((payload) => dispatch(openMobileSliderPageAction(payload)), [dispatch]);
    const { getLangPath } = useLangContext();
    const [cancelingOrder, setCancelingOrder] = useState(false);

    const cancelOrderHandler = useCallback(() => {
        setCancelingOrder(true);
        cancelOrder({
            orderCode: orderInfo?.orderCode,
            onSuccessCallback: () => onClose('close_order'),
        });
    }, [cancelOrder, onClose, orderInfo]);

    const asset = useMemo(
        () => _.find(assets, { symbol: orderInfo?.instrument }) || ({} as ILiteTradeAsset),
        [assets, orderInfo],
    ) as ILiteTradeAsset;

    const clickAssetHandler = useCallback((e) => {
            e.preventDefault();
            if (openAssetOnClick) {
                dispatch(locationSlice.actions.openLocationAction({
                    name: LOCATION_NAMES.ASSET,
                    searchParams: {
                        symbol: orderInfo?.instrument,
                    }
                }));
            }
            onClose('asset');
        },
        [dispatch, onClose, openAssetOnClick, orderInfo?.instrument],
    );

    const editPositionHandler = useCallback(() => {
        if (
            features[FEATURES.CREATE_ORDER_2] &&
            (!isOpenOrderV2AbTestOptionActive || openOrderV2AbTestOption === AB_TEST_OPEN_ORDER_V2_OPTIONS.ENABLED)
        ) {
            const searchParams = new URLSearchParams(location.search);
            const name = searchParams.getAll('s') as IMobileSliderPageId[];
            name.push(MOBILE_SLIDER_PAGES_IDS.ORDER_UPDATE_2);
            openMobileSliderPage({
                name,
                'order-id': orderId,
            });
            onClose('edit_position');
        } else {
            openMobileSliderPage({
                name: MOBILE_SLIDER_PAGES_IDS.POSITION_UPDATE,
                'order-id': orderId,
            });
            onClose('edit_order');
        }
    }, [
        features,
        isOpenOrderV2AbTestOptionActive,
        location.search,
        onClose,
        openMobileSliderPage,
        openOrderV2AbTestOption,
        orderId,
    ]);

    const handleCloseCommon = useCallback(() => {
        onClose('close');
    }, [onClose]);

    if (!orderInfo) {
        return <Loader show={true} />;
    }

    return <Slider
            showCloseButton={false}
            placement={'bottom'}
            show={true}
            onClose={handleCloseCommon}
            style={sliderStyle}
        >
            {!!cancelingOrder && <Loader show={true} />}
            <div className="d-flex flex-column">
                <SlideMobileModalHeaderStyled>
                    <div className="close" onClick={handleCloseCommon}>
                        <BinIcon
                            name={'IcClose'}
                            size={24}
                            style={{
                                fill: 'rgba(255, 255, 255, 0.6)',
                            }}
                        />
                    </div>
                </SlideMobileModalHeaderStyled>
                <SlideMobileModalBoxStyled>
                    <SlideMobileOrderModalAssetBoxStyled onClick={clickAssetHandler}>
                        <div className="f28">
                            <IconAsset symbol={orderInfo?.instrument} />
                        </div>
                        <div className="d-flex flex-column">
                            <div className="f16">{asset.info?.description}</div>
                            <div className="fw500 text-secondary">
                                <LiteTradePortfolioOrderInfo
                                    item={{
                                        ...orderInfo,
                                        assetInstance,
                                    }}
                                />
                            </div>
                        </div>
                        <div className="flex-1" />
                        <BinIcon name={'IcMoveRight'} />
                    </SlideMobileOrderModalAssetBoxStyled>
                    <SlideMobileModalInfoRowStyled className="mt-2 no-border">
                        <div className="label">
                            {getLangPath(
                                withSpread
                                    ? 'lite_version.order.current_price_spread'
                                    : 'lite_version.order.current_price',
                            )}
                        </div>
                        <div className="big_price">
                            <AssetRelevantPrice symbol={symbol} withSpread={withSpread} />
                        </div>
                    </SlideMobileModalInfoRowStyled>
                    {orderInfo?.type === ORDER_TYPES.LIMIT &&
                        <SlideMobileModalInfoRowStyled>
                            <div className="label">{getLangPath('lite_version.order.opening_price')}</div>
                            <div className="price">{assetInstance.amountToFixed(orderInfo?.legs[0].price)}</div>
                        </SlideMobileModalInfoRowStyled>
                    }
                    {orderInfo?.type === ORDER_TYPES.STOP &&
                        <SlideMobileModalInfoRowStyled>
                            <div className="label">{getLangPath('lite_version.order.opening_price')}</div>
                            <div className="price">{assetInstance.amountToFixed(orderInfo?.legs[0].price)}</div>
                        </SlideMobileModalInfoRowStyled>
                    }
                    {!!orderStopLoss &&
                        <SlideMobileModalInfoRowStyled>
                            <div className="label">{getLangPath('lite_version.order.stop_loss')}</div>
                            <div className="price">{assetInstance.amountToFixed(orderStopLoss.legs[0].price)}</div>
                        </SlideMobileModalInfoRowStyled>
                    }
                    {!!orderTakeProfit &&
                        <SlideMobileModalInfoRowStyled>
                            <div className="label">{getLangPath('lite_version.order.take_profit')}</div>
                            <div className="price">{assetInstance.amountToFixed(orderTakeProfit.legs[0].price)}</div>
                        </SlideMobileModalInfoRowStyled>
                    }
                </SlideMobileModalBoxStyled>

            {!!assetInstance.isTradingNow()
                ? <>
                    <div className="d-flex flex-column mt-3">
                        <Button variant="secondary" onClick={editPositionHandler}>
                            {getLangPath('lite_version.portfolio.edit_order')}
                        </Button>
                    </div>
                    <div className="d-flex justify-content-center mt-3">
                        <Button variant="tertiary negative" onClick={cancelOrderHandler}>
                            {getLangPath('lite_version.portfolio.cancel_order')}
                        </Button>
                    </div>
                </>
                : <>
                    <MobilePositionUnavailableStyled>
                        {getLangPath('lite_version.portfolio.inactive_by_schedule')}
                    </MobilePositionUnavailableStyled>
                </>
            }
        </div>
    </Slider>;
};

export const sliderStyle = {
    backgroundColor: 'var(--color-background-secondary-to-primary)',
};

export default SlideMobileOrderModal;
